// Mango Personal Account Types

// Subscription tiers
export type SubscriptionTier = 'free' | 'social' | 'pro' | 'premium';

export interface SubscriptionPlan {
  id: SubscriptionTier;
  name: string;
  price: number; // Monthly price in USD
  annualPrice?: number;
  features: string[];
  limits: {
    productListings: number; // 0 = none, -1 = unlimited
    storageGB: number;
    historyMonths: number; // Order/receipt history retention
    familyMembers: number; // 0 = none
  };
}

export const SUBSCRIPTION_PLANS: Record<SubscriptionTier, SubscriptionPlan> = {
  free: {
    id: 'free',
    name: 'Mango Free',
    price: 0,
    features: [
      'Unified order history (12 months)',
      'Digital receipts & tickets',
      'Conversations with businesses',
      'Subscription management',
      'Mango Wallet (receive credits)',
      'Basic wishlist',
      'Business directory',
      'Reviews & ratings',
      'Push notifications',
    ],
    limits: {
      productListings: 0,
      storageGB: 0,
      historyMonths: 12,
      familyMembers: 0,
    },
  },
  social: {
    id: 'social',
    name: 'Mango Social',
    price: 3,
    annualPrice: 30,
    features: [
      'Everything in Free',
      'Create & sell products (3 max)',
      'Create personal events',
      'Customer-to-customer messaging',
      'Join & create groups',
      'Share products with friends',
      'Price alerts',
      'Gift registry',
      'Basic planning tools',
      'Shopping lists with calendar sync',
    ],
    limits: {
      productListings: 3,
      storageGB: 1,
      historyMonths: 24,
      familyMembers: 0,
    },
  },
  pro: {
    id: 'pro',
    name: 'Mango Pro',
    price: 15,
    annualPrice: 120,
    features: [
      'Everything in Social',
      'Sell unlimited products',
      'Personal finance dashboard',
      'Spending analytics',
      'Budget planner',
      'Receipt scanner',
      'Document vault (5GB)',
      'Warranty tracker',
      'Smart reminders',
      'Family accounts (up to 3)',
      'Extended history (unlimited)',
    ],
    limits: {
      productListings: 50,
      storageGB: 5,
      historyMonths: -1, // Unlimited
      familyMembers: 3,
    },
  },
  premium: {
    id: 'premium',
    name: 'Mango Premium',
    price: 35,
    annualPrice: 300,
    features: [
      'Everything in Pro',
      'Unlimited product listings',
      'Personal AI assistant',
      'Bank connection',
      'Advanced analytics',
      'Priority support',
      'VIP access to sales',
      'Exclusive discounts',
      'Family accounts (up to 5)',
      'Document vault (25GB)',
      'Ad-free experience',
    ],
    limits: {
      productListings: -1, // Unlimited
      storageGB: 25,
      historyMonths: -1,
      familyMembers: 5,
    },
  },
};

/** Social handles for tagging in Mango Post (e.g. @username) */
export interface SocialLinksForTagging {
  x?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
}

// Shareable profile - filled in Mango Personal, shared with businesses when accepting staff invitations
export interface StaffProfile {
  bio?: string;
  emergencyContacts?: Array<{ name: string; phone: string; relationship: string }>;
  instagram?: string;
  facebook?: string;
  linkedin?: string;
  website?: string;
  /** Handles for tagging in posts (X @handle, IG @handle, etc.) */
  socialLinks?: SocialLinksForTagging;
  bankAccountName?: string;
  bankAccountNumber?: string;
  bankBSB?: string;
  bankRoutingNumber?: string;
  taxFileNumber?: string;
  taxId?: string;
  taxIdType?: string;
  abn?: string;
  category?: string;
}

// User account
export interface MangoUser {
  id: string;
  mangoId: string; // MP-XX-123456 format
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  avatarUrl?: string;
  subscription: SubscriptionTier;
  subscriptionExpiresAt?: string;
  createdAt: string;
  wallet: MangoWallet;
  settings: UserSettings;
  familyMembers?: FamilyMember[];
  staffProfile?: StaffProfile | null;
}

export interface FamilyMember {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
  permissions: {
    canPurchase: boolean;
    spendingLimit?: number;
    canViewHistory: boolean;
    canManageSubscriptions: boolean;
  };
  createdAt: string;
}

export interface UserSettings {
  notifications: {
    orders: boolean;
    messages: boolean;
    promotions: boolean;
    priceAlerts: boolean;
  };
  privacy: {
    showProfile: boolean;
    allowMessages: boolean;
    showActivity: boolean;
  };
  display: {
    theme: 'light' | 'dark' | 'system';
    language: string;
    currency: string;
  };
}

// Mango Wallet / Credits (Cryptocurrency)
export interface MangoWallet {
  id: string;
  address: string; // Blockchain wallet address
  mangoCredits: number; // Mango token balance
  pendingCredits: number; // Pending from transactions
  weight: number; // Trust weight for debt capacity
  transactionHistory: WalletTransaction[];
}

export interface WalletTransaction {
  id: string;
  type: 'receive' | 'send' | 'earn' | 'spend' | 'convert';
  amount: number;
  fromAddress?: string;
  toAddress?: string;
  fromName?: string;
  toName?: string;
  description: string;
  businessId?: string;
  businessName?: string;
  status: 'pending' | 'confirmed' | 'failed';
  createdAt: string;
  confirmedAt?: string;
}

// Orders from all Mango businesses
export interface Order {
  id: string;
  orderNumber: string;
  businessId: string;
  businessName?: string;
  business?: { id?: string; name?: string; mangoId?: string };
  businessMangoId?: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  shipping: number;
  total: number;
  paymentMethod: string;
  mangoCreditsPaid?: number;
  status?: 'pending' | 'confirmed' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
  fulfillmentStatus?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  shippingAddress?: Address;
  createdAt: string;
  updatedAt: string;
}

export interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  productImage?: string;
  quantity: number;
  price: number;
  variant?: string;
}

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

// Event Tickets
export interface EventTicket {
  id: string;
  eventId: string;
  eventName?: string;
  businessId?: string;
  businessName?: string;
  ticketType?: string;
  ticketNumber?: string;
  qrCode?: string;
  eventDate?: string;
  eventLocation?: string;
  price?: number;
  status?: 'valid' | 'used' | 'cancelled' | 'expired';
  purchasedAt?: string;
  /** API returns nested event from backend */
  event?: { id?: string; name?: string; date?: string; location?: string; coverImage?: string };
}

// Subscriptions (to businesses)
export interface BusinessSubscription {
  id: string;
  businessId: string;
  businessName: string;
  businessMangoId: string;
  planName: string;
  amount: number;
  interval: 'weekly' | 'monthly' | 'yearly';
  status: 'active' | 'paused' | 'cancelled';
  nextBillingDate: string;
  createdAt: string;
}

// Conversations
export interface Conversation {
  id: string;
  businessId: string;
  businessName: string;
  businessMangoId: string;
  businessLogoUrl?: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
  status: 'open' | 'closed' | 'archived';
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  senderType: 'customer' | 'business';
  senderName: string;
  content: string;
  attachments?: MessageAttachment[];
  readAt?: string;
  createdAt: string;
}

export interface MessageAttachment {
  id: string;
  type: 'image' | 'file' | 'order' | 'product';
  url: string;
  name: string;
  size?: number;
}

// Marketplace Listings (for Social+ tiers)
export interface MarketplaceListing {
  id: string;
  sellerId: string;
  sellerName: string;
  sellerMangoId: string;
  sellerRating: number;
  title: string;
  description: string;
  price: number;
  acceptMangoCredits: boolean;
  images: string[];
  category: string;
  condition: 'new' | 'like_new' | 'good' | 'fair' | 'poor';
  location: string;
  status: 'active' | 'sold' | 'reserved' | 'expired';
  views: number;
  saves: number;
  createdAt: string;
  expiresAt?: string;
}

// Social Features
export interface UserConnection {
  id: string;
  userId: string;
  userName: string;
  userMangoId: string;
  userAvatarUrl?: string;
  connectionType: 'friend' | 'following' | 'family';
  trustWeight: number; // Multiplier for trust (1.0-1.5)
  createdAt: string;
}

export interface Group {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  memberCount: number;
  isPublic: boolean;
  category: string;
  createdBy: string;
  createdAt: string;
}

// Personal Events (for Social+ tiers)
export interface PersonalEvent {
  id: string;
  creatorId: string;
  creatorName: string;
  title: string;
  description: string;
  date: string;
  endDate?: string;
  location: string;
  isVirtual: boolean;
  virtualLink?: string;
  imageUrl?: string;
  invitees: EventInvitee[];
  isPublic: boolean;
  category: string;
  createdAt: string;
}

export interface EventInvitee {
  userId: string;
  userName: string;
  status: 'pending' | 'accepted' | 'declined' | 'maybe';
  respondedAt?: string;
}

// Wishlist
export interface WishlistItem {
  id: string;
  productId: string;
  productName: string;
  productImage?: string;
  businessId: string;
  businessName: string;
  price: number;
  originalPrice?: number;
  addedAt: string;
  priceAlertEnabled: boolean;
  targetPrice?: number;
}

// Gift Registry
export interface GiftRegistry {
  id: string;
  name: string;
  description?: string;
  eventDate?: string;
  eventType: 'birthday' | 'wedding' | 'baby_shower' | 'housewarming' | 'other';
  isPublic: boolean;
  shareUrl: string;
  items: GiftRegistryItem[];
  createdAt: string;
}

export interface GiftRegistryItem {
  id: string;
  productId: string;
  productName: string;
  productImage?: string;
  businessId: string;
  businessName: string;
  price: number;
  quantity: number;
  purchasedQuantity: number;
  priority: 'high' | 'medium' | 'low';
}

// Finance Tracking (Pro+ tiers)
export interface FinanceCategory {
  id: string;
  name: string;
  icon: string;
  color: string;
  type: 'income' | 'expense';
  budget?: number;
}

export interface FinanceTransaction {
  id: string;
  categoryId: string;
  amount: number;
  description: string;
  date: string;
  businessId?: string;
  businessName?: string;
  orderId?: string;
  isRecurring: boolean;
  attachmentUrl?: string;
  createdAt: string;
}

export interface Budget {
  id: string;
  categoryId: string;
  categoryName: string;
  amount: number;
  spent: number;
  period: 'weekly' | 'monthly' | 'yearly';
  startDate: string;
}

// Document Vault (Pro+ tiers)
export interface VaultDocument {
  id: string;
  name: string;
  type: 'receipt' | 'warranty' | 'contract' | 'insurance' | 'other';
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  thumbnailUrl?: string;
  expiryDate?: string;
  tags: string[];
  linkedProductId?: string;
  linkedOrderId?: string;
  createdAt: string;
}

// Saved/Followed Businesses
export interface FollowedBusiness {
  id: string;
  businessId: string;
  businessName: string;
  businessMangoId: string;
  businessLogoUrl?: string;
  businessCategory: string;
  notificationsEnabled: boolean;
  followedAt: string;
}

// Reviews
export interface Review {
  id: string;
  businessId: string;
  businessName: string;
  productId?: string;
  productName?: string;
  eventId?: string;
  eventName?: string;
  rating: number; // 1-5
  title?: string;
  content: string;
  images?: string[];
  helpfulCount: number;
  response?: {
    content: string;
    respondedAt: string;
  };
  createdAt: string;
}

// API Response types
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
